
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { ToggleLeft, ToggleRight, UserPlus, Trash2 } from "lucide-react";
import { UserTable } from "./UserTable";
import { UserCreditsForm } from "./UserCreditsForm";
import { AddUserForm } from "./AddUserForm";

// Demo data
const demoUsers = [
  { id: 1, username: "demo", credits: 50, active: true },
  { id: 2, username: "user1", credits: 120, active: true },
  { id: 3, username: "user2", credits: 75, active: true }
];

export default function UserManagement() {
  const [users, setUsers] = useState(demoUsers);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [creditsToAdd, setCreditsToAdd] = useState(0);

  const handleAddCredits = () => {
    if (selectedUser === null) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ επιλέξτε έναν χρήστη",
        variant: "destructive"
      });
      return;
    }
    
    setUsers(users.map(user => 
      user.id === selectedUser 
        ? { ...user, credits: user.credits + creditsToAdd } 
        : user
    ));
    
    toast({
      title: "Επιτυχία",
      description: `Προστέθηκαν ${creditsToAdd} credits στον χρήστη ${users.find(u => u.id === selectedUser)?.username}`
    });
    
    setSelectedUser(null);
    setCreditsToAdd(0);
  };

  const handleToggleUserStatus = (userId: number) => {
    setUsers(users.map(user => 
      user.id === userId 
        ? { ...user, active: !user.active } 
        : user
    ));
    
    const user = users.find(u => u.id === userId);
    const newStatus = !user?.active;
    
    toast({
      title: "Επιτυχία",
      description: `Ο χρήστης ${user?.username} ${newStatus ? 'ενεργοποιήθηκε' : 'απενεργοποιήθηκε'} επιτυχώς`
    });
  };

  const handleAddUser = (username: string, password: string, initialCredits: number) => {
    // Check if username already exists
    if (users.some(user => user.username === username)) {
      toast({
        title: "Σφάλμα",
        description: "Το όνομα χρήστη υπάρχει ήδη",
        variant: "destructive"
      });
      return;
    }

    // Add new user
    const newUser = {
      id: Math.max(...users.map(u => u.id)) + 1,
      username,
      credits: initialCredits,
      active: true
    };

    setUsers([...users, newUser]);
    
    toast({
      title: "Επιτυχία",
      description: `Ο χρήστης ${username} προστέθηκε επιτυχώς με ${initialCredits} credits`
    });
  };

  const handleDeleteUser = (userId: number) => {
    // Remove user
    setUsers(users.filter(user => user.id !== userId));
    
    // If the deleted user was selected, clear selection
    if (selectedUser === userId) {
      setSelectedUser(null);
      setCreditsToAdd(0);
    }
    
    const user = users.find(u => u.id === userId);
    toast({
      title: "Επιτυχία",
      description: `Ο χρήστης ${user?.username} διαγράφηκε επιτυχώς`
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Λίστα Χρηστών</CardTitle>
        </CardHeader>
        <CardContent>
          <UserTable 
            users={users} 
            onSelectUser={setSelectedUser}
            onToggleStatus={handleToggleUserStatus}
            onDeleteUser={handleDeleteUser}
          />
        </CardContent>
      </Card>
      
      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Προσθήκη Credits</CardTitle>
          </CardHeader>
          <CardContent>
            <UserCreditsForm
              selectedUser={selectedUser ? users.find(u => u.id === selectedUser)?.username || "" : ""}
              creditsToAdd={creditsToAdd}
              onCreditsChange={setCreditsToAdd}
              onAddCredits={handleAddCredits}
              disabled={!selectedUser || creditsToAdd <= 0}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Προσθήκη Νέου Χρήστη</CardTitle>
          </CardHeader>
          <CardContent>
            <AddUserForm onAddUser={handleAddUser} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
